import { ChevronLeft, Heart, BookOpen, BarChart3, Target } from "lucide-react";
import { useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { UserCircle, Settings, LogOut } from "lucide-react";

const SECTIONS = [
  {
    id: "about",
    icon: Heart,
    title: "Built for Churches That Want to Lead Well",
    paragraphs: [
      "iChurch Data was created to help churches steward their people and resources with clarity, integrity, and purpose.",
      "Too many churches are held back by scattered tools, manual processes, and unclear financial visibility. We believe church leaders should not have to struggle with systems just to stay organized.",
      "Our mission is simple. Remove complexity so churches can focus on ministry, serve people better, and make confident decisions.",
      "When your systems are clear, your leadership becomes stronger. When your data is organized, your impact grows.",
    ],
  },
  {
    id: "why",
    icon: Target,
    title: "More Than Software",
    paragraphs: [
      "Churches are not businesses, but they still require structure, accountability, and wise stewardship.",
      "Every member matters. Every dollar has a purpose. Every decision impacts the mission.",
      "iChurch Data exists to support that responsibility by giving leaders the clarity they need to lead with confidence and transparency.",
    ],
  },
  {
    id: "fund-accounting",
    icon: BarChart3,
    title: "Fund Accounting Built for Ministry",
    paragraphs: [
      "iChurch Data is built on fund accounting — a system designed specifically for churches and nonprofits.",
      "Instead of focusing on profit, fund accounting focuses on accountability. It tracks where money comes from, where it is going, and how it is used across each ministry.",
      "This means designated funds stay aligned with their purpose, and leaders always have a clear picture of their financial position.",
      "With fund accounting, your church can steward resources with integrity, build trust, and stay aligned with its mission.",
    ],
  },
  {
    id: "closing",
    icon: BookOpen,
    title: "Focus on What Matters Most",
    paragraphs: [
      "You should not have to choose between managing your church and leading it.",
      "iChurch Data gives you the clarity, structure, and confidence to do both.",
    ],
  },
];

const AboutPage = () => {
  const { user, role, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = !!user;
  const displayName = user?.email?.split("@")[0] || "User";
  const userInitials = (displayName.slice(0, 2) || "ME").toUpperCase();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname]);

  return (
    <div className="min-h-screen brand-page-bg text-slate-900">
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -top-32 left-0 h-96 w-96 rounded-full bg-[#dbe9ff] blur-3xl" />
        <div className="absolute top-20 right-0 h-80 w-80 rounded-full bg-[#f8efe2] blur-3xl" />
        <div className="absolute bottom-10 left-1/3 h-72 w-72 rounded-full bg-[#e6f4ee] blur-3xl" />
      </div>

      <header className="fixed inset-x-0 top-0 z-40 border-b border-slate-200/70 bg-[#fbfdff]/92 backdrop-blur">
        <div className="mx-auto flex w-full max-w-6xl items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2">
            <img src="/ichurch-logo.svg" alt="iChurch Data" className="h-8 w-8 rounded-xl object-cover" />
            <span className="text-lg font-semibold tracking-tight text-slate-900">iChurch Data</span>
          </Link>

          <div className="flex items-center gap-1 text-sm font-medium text-slate-600">
            <Button variant="ghost" className="hidden text-slate-700 md:inline-flex" asChild>
              <Link to="/#features">Features</Link>
            </Button>
            <Button variant="ghost" className="hidden text-slate-700 md:inline-flex" asChild>
              <Link to="/#pricing">Pricing</Link>
            </Button>
            <Button variant="ghost" className="hidden text-slate-700 md:inline-flex" asChild>
              <Link to="/about">About</Link>
            </Button>
            {isAuthenticated ? (
              <>
                <Button variant="ghost" className="text-slate-700" asChild>
                  <Link to="/dashboard">Dashboard</Link>
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="flex items-center gap-2 p-1 rounded-lg hover:bg-secondary transition-colors" aria-label="Open profile menu">
                      <Avatar className="h-9 w-9 border-2 border-border">
                        <AvatarFallback className="bg-primary text-primary-foreground text-sm font-semibold">
                          {userInitials}
                        </AvatarFallback>
                      </Avatar>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel className="font-normal">
                      <div className="flex flex-col space-y-1">
                        <p className="text-sm font-medium text-foreground">{displayName}</p>
                        <p className="text-xs text-muted-foreground">{user?.email}</p>
                        {role && <Badge variant="secondary" className="w-fit text-xs capitalize mt-1">{role}</Badge>}
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate("/profile")} className="cursor-pointer">
                      <UserCircle className="w-4 h-4 mr-2" /> Profile
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/settings")} className="cursor-pointer">
                      <Settings className="w-4 h-4 mr-2" /> Settings
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => signOut()}
                      className="cursor-pointer text-destructive focus:text-destructive"
                    >
                      <LogOut className="w-4 h-4 mr-2" /> Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Button variant="ghost" className="text-slate-700" asChild>
                  <Link to="/login">Sign in</Link>
                </Button>
                <Button className="bg-[#1e3a8a] hover:bg-[#162d70]" asChild>
                  <Link to="/register">Sign Up</Link>
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="relative z-10 pt-[69px]">
        {/* Hero */}
        <section className="mx-auto w-full max-w-4xl px-6 pt-12 pb-8">
          <Link to="/" className="inline-flex items-center gap-1 text-sm text-[#1e3a8a] font-medium hover:underline mb-6">
            <ChevronLeft className="h-4 w-4" /> Back to Home
          </Link>
          <h1 className="text-3xl font-bold tracking-tight text-[#1e3a8a] sm:text-4xl">
            About iChurch Data
          </h1>
          <p className="mt-3 text-lg text-slate-600 max-w-3xl">
            Church management software built with stewardship, integrity, and service at its core.
          </p>
        </section>

        {/* Content Sections */}
        <div className="mx-auto w-full max-w-4xl px-6 pb-20 space-y-6">
          {SECTIONS.map((section, idx) => (
            <section key={section.id} id={section.id}>
              <div className="rounded-2xl border border-[#d9e3ef] bg-gradient-to-br from-white to-[#f8fbff] p-6 sm:p-10 shadow-sm">
                <div className="flex items-center gap-3 mb-6">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-[#e7effc] to-[#f4efe8]">
                    <section.icon className="h-5 w-5 text-[#1e3a8a]" />
                  </div>
                  <h2 className="text-xl font-bold tracking-tight text-[#1e3a8a] sm:text-2xl">
                    {section.title}
                  </h2>
                </div>
                <div className="space-y-4">
                  {section.paragraphs.map((p, i) => (
                    <p key={i} className="text-[15px] leading-7 text-slate-700">
                      {p}
                    </p>
                  ))}
                </div>
              </div>
            </section>
          ))}

          {/* CTA */}
          <div className="text-center pt-4">
            <p className="text-lg font-medium text-slate-700 mb-5">
              Ready to simplify how your church is managed?
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Button className="bg-[#1e3a8a] hover:bg-[#162d70] px-8 h-11 text-sm" asChild>
                <Link to="/register">Get Started Free</Link>
              </Button>
              <Button variant="outline" className="px-8 h-11 text-sm border-slate-300" asChild>
                <Link to="/#features">See Features</Link>
              </Button>
            </div>
          </div>
        </div>
      </main>

      <footer className="relative z-10 border-t border-slate-200 bg-gradient-to-b from-white to-[#f8fbff]">
        <div className="mx-auto w-full max-w-6xl px-6 py-10 text-sm text-slate-600">
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div>
              <p className="text-base font-semibold text-slate-900">iChurch Data LLC</p>
              <p className="mt-1">1260 Bees Ferry Rd</p>
              <p>Charleston, SC 29414</p>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/privacy" className="hover:text-slate-900">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-slate-900">Terms of Service</Link>
              <Link to="/login" className="hover:text-slate-900">Sign in</Link>
            </div>
          </div>
          <div className="mt-6 border-t border-slate-200 pt-5">
            <p>© 2026 iChurch Data. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AboutPage;
